<template>
	<div class="card !shadow-none !border-0">
		<!-- Loading State -->
		<div v-if="isLoading" class="flex items-center justify-center py-10">
			<i class="tabler:loader-2 w-8 h-8 animate-spin text-primary"></i>
			<span class="ml-2">Loading quiz...</span>
		</div>
		<!-- Error State -->
		<div v-else-if="fetchError" class="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:alert-circle text-red-600 text-lg mr-2"></i>
				<span class="text-red-800">{{ fetchError }}</span>
			</div>
		</div>
		<!-- Quiz Content -->
		<template v-else>
		<!-- DEVELOPER TESTING TOGGLE (remove in production) -->
		<!-- <div class="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
			<div class="flex items-center justify-between">
				<div class="flex items-center">
					<i class="tabler:settings text-yellow-600 text-lg mr-2"></i>
					<span class="font-semibold text-yellow-800">DEVELOPER TESTING</span>
				</div>
				<div class="flex gap-2">
					<button 
						@click="testingMode = 'test'"
						:class="[
							'px-3 py-1 rounded text-sm font-medium transition-all',
							currentMode === 'test' ? 'bg-green-500 text-white' : 'bg-white text-green-600 border border-green-300 hover:bg-green-50'
						]"
					>
						🧪 Test Mode
					</button>
					<button 
						@click="testingMode = 'exam'"
						:class="[
							'px-3 py-1 rounded text-sm font-medium transition-all',
							currentMode === 'exam' ? 'bg-red-500 text-white' : 'bg-white text-red-600 border border-red-300 hover:bg-red-50'
						]"
					>
						🎓 Exam Mode
					</button>
				</div>
			</div>
		</div> -->

		<!-- Mode Indicator -->
		<!-- <div v-if="isCurrentlyExamMode" class="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:shield-check text-red-600 text-lg mr-2"></i>
				<span class="font-semibold text-red-800">EXAM MODE ACTIVE</span>
				<span class="ml-2 text-sm text-red-600">(Single selection • No feedback • No back button)</span>
			</div>
		</div>
		<div v-else class="mb-4 p-3 bg-green-50 border-l-4 border-green-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:clipboard-check text-green-600 text-lg mr-2"></i>
				<span class="font-semibold text-green-800">TEST MODE ACTIVE</span>
				<span class="ml-2 text-sm text-green-600">(Multiple selection • Feedback • Can go back)</span>
			</div>
		</div> -->
		<QuizStepper orientation="vertical">
			<StepperPanel v-for="( question, index) in questions" :key="index" :active="currentQuestionIndex === index"
			:header="labels.questionLabel + (index + 1)"
			:class="{ 
				'exam-locked': isCurrentlyExamMode && completedQuestions.has(index),
				'exam-current': isCurrentlyExamMode && index === currentQuestionIndex 
			}"
			>
			<template #content="{ prevCallback, nextCallback }">
				<!-- Quiz content - disabled when completed in exam mode -->
				<div class="flex flex-col" :class="{ 
					'opacity-50 pointer-events-none': isCurrentlyExamMode && completedQuestions.has(index) 
				}">
						<InplaceEdit
							:inputProp="{
								propName: 'content',
								inputType: 'quiz',
								content: question
							}"
							method="PUT"
							:href="question._links?.self.href"
							blockTitle="Edit Question"
							:showAssistant="true"
						>
							<span class="font-semibold">{{ question.question }}</span>
						</InplaceEdit>

						<div class="py-5 flex-auto flex flex-col justify-center items-start font-semibold">
							<div v-for="( answer, aIndex) in question.answers" :key="aIndex"
								class="p-4 w-full bg-gray-100">
								<div class="text-md flex">
									<!-- EXAM MODE: Radio button (single selection) -->
									<QuizRadioButton v-if="isCurrentlyExamMode"
										:value="aIndex" 
										v-model="selectedAnswers[index]"
										:inputId="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										:name="'question-' + index" />
									
									<!-- TEST MODE: Checkbox (multiple selection) - DEFAULT -->
									<QuizCheck v-else
										:value="aIndex" 
										v-model="selectedAnswers[index]"
										:inputId="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										:name="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length" />
										
									<label :for="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										class="ml-2 font-normal">
										{{ answer.answer }}
									</label>
								</div>
								<!-- Show feedback: always in test mode, never in exam mode -->
								<div v-if="isCurrentlyTestMode && isAnswerSelected(index, aIndex)">
									<div class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-success/40 bg-success-100/10 text-dark"
										v-if=" answer.isCorrect ">
										<i
											class="tabler:circle-check !text-[35px] text-success max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"></i>
										<span class="text-sm">{{ answer.explanation }} </span>
									</div>
									<div v-else
										class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-danger/40 bg-danger-100/10 text-dark">
										<i
											class="tabler:circle-x !text-[35px] text-danger max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"></i>

										<span class="text-sm">{{ answer.explanation }}</span>
									</div>
								</div>
							</div>
						</div>
				</div>
				<div class="flex pt-4 justify-between">
						<!-- Back button: hidden in exam mode -->
						<EwButton v-if="index && isCurrentlyTestMode" :label="'Back'" type="secondary" :icon="'tabler:arrow-narrow-left'"
							@click="prevCallback" />
						
						<!-- Next button: different labels for different modes -->
						<EwButton v-if=" index < questions.length - 1 " type="success" 
							:label="isCurrentlyExamMode ? 'Next Question' : 'Next'"
							:iconAppend="'tabler:arrow-narrow-right'" 
							:loading="isSubmittingAnswer"
							@click="() => handleExamNext(nextCallback, index)" />
						
						<!-- Finish button: different labels and styles for different modes -->
						<EwButton v-if=" questions.length - 1 === index " 
							:type="isCurrentlyExamMode ? 'danger' : 'success'" 
							:label="isCurrentlyExamMode ? 'Submit Exam' : 'Finish'"
							:iconAppend="isCurrentlyExamMode ? 'tabler:send' : 'tabler:check'" 
							@click="handleClose" />
					</div>
				</template>
			</StepperPanel>
		</QuizStepper>
		</template>
	</div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { messages } from '@/assets/language';
import type { LanguageCodes, QuizQuestionsItem } from '@/types'
import InplaceEdit from './InplaceEdit/InplaceEdit.vue';
import { useAppStore } from '@/stores/app-store'


const { currentLng } = useAppStore()
const labels = computed( () => {
	const key = currentLng.value as keyof typeof messages
	return messages[key] ?? {}
});

type Answer = {
  answer: string;
  answerLabel: string;
  explanation: string;
  isCorrect: boolean;
};

type Question = {
  question: string;
  answers: Answer[];
  _links?: { self: { href: string } };
};

// Add mode type for exam/test detection
type QuizMode = 'test' | 'exam';

const props = defineProps<{
  selectedLanguage?: LanguageCodes;
  mode?: QuizMode; // Add mode prop - defaults to 'test' if not provided
  overrideURL?: string; // URL with parameters to fetch quiz data
  jsonData: {
    preQuiz: { questions: QuizQuestionsItem[] };
	_links: { preQuiz: { href: string }; preQuizEdit: { href: string } };
  };
}>();

// Loading and error states for fetch
const isLoading = ref(false);
const fetchError = ref<string | null>(null);
const fetchedData = ref<{ preQuiz: { questions: QuizQuestionsItem[] } } | null>(null);

// Use fetched data if available, otherwise fall back to jsonData
const questions = computed(() => {
  if (fetchedData.value?.preQuiz?.questions) {
    return fetchedData.value.preQuiz.questions;
  }
  return props.jsonData.preQuiz.questions;
});

// Fetch quiz data from overrideURL
const fetchQuizData = async () => {
  if (!props.overrideURL) {
    console.log('[PreQuiz] No overrideURL provided, using jsonData');
    return;
  }
  
  isLoading.value = true;
  fetchError.value = null;
  
  try {
    console.log('[PreQuiz] Fetching quiz from:', props.overrideURL);
    const response = await fetch(props.overrideURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.log('[PreQuiz] Response is not JSON, using jsonData');
      return;
    }
    
    if (!response.ok) {
      console.log('[PreQuiz] HTTP error, using jsonData:', response.status);
      return;
    }
    
    const data = await response.json();
    console.log('[PreQuiz] Fetched data:', data);
    
    if (data?.preQuiz?.questions) {
      fetchedData.value = data;
    } else {
      console.log('[PreQuiz] Invalid response structure, using jsonData');
    }
  } catch (error) {
    console.log('[PreQuiz] Fetch error, using jsonData:', error);
    // Don't show error to user, just use jsonData silently
  } finally {
    isLoading.value = false;
  }
};

// Fetch on mount if overrideURL is provided
onMounted(() => {
  if (props.overrideURL) {
    fetchQuizData();
  }
});

// DEVELOPER TESTING: Override mode for testing purposes
const testingMode = ref<QuizMode>('test');

// Smart data structure: test mode uses arrays, exam mode uses single numbers
const selectedAnswers = ref<Array<Array<number> | number>>([]);

// Initialize and update selectedAnswers when questions change
watch(questions, (newQuestions) => {
  selectedAnswers.value = newQuestions.map(() => testingMode.value === 'exam' ? -1 : []);
}, { immediate: true });

// Update when testing mode changes
watch(testingMode, (newMode) => {
  selectedAnswers.value = questions.value.map(() => newMode === 'exam' ? -1 : []);
});

const currentQuestionIndex = ref( 0 );
const emits = defineEmits( ['close', 'submit'] );

// Current active mode: uses prop if provided, otherwise uses testing override
const currentMode = computed(() => props.mode || testingMode.value);

// Update mode detection to use currentMode
const isCurrentlyExamMode = computed(() => currentMode.value === 'exam');
const isCurrentlyTestMode = computed(() => currentMode.value === 'test');

// EXAM MODE: Track completed/locked questions
const completedQuestions = ref<Set<number>>(new Set());
const isSubmittingAnswer = ref(false);

// Legacy mode detection (keeping for compatibility)
const isExamMode = computed(() => props.mode === 'exam');
const isTestMode = computed(() => !props.mode || props.mode === 'test');

// Helper function to check if answer is selected (works for both modes)
const isAnswerSelected = (questionIndex: number, answerIndex: number) => {
  if (isCurrentlyExamMode.value) {
    return selectedAnswers.value[questionIndex] === answerIndex;
  } else {
    return (selectedAnswers.value[questionIndex] as number[]).includes(answerIndex);
  }
};

// EXAM MODE: Submit answer to backend and lock question
const submitExamAnswer = async (questionIndex: number) => {
  if (!isCurrentlyExamMode.value || completedQuestions.value.has(questionIndex)) {
    return;
  }

  const question = questions.value[questionIndex];
  const selectedAnswer = selectedAnswers.value[questionIndex];
  
  if (selectedAnswer === -1) {
    // No answer selected
    return;
  }

  isSubmittingAnswer.value = true;

  try {
    // Prepare exam answer data
    const examAnswerData = {
      questionIndex,
      selectedAnswer,
      completedAt: new Date().toISOString(),
      mode: 'exam'
    };

    // Send PUT request to backend
    const response = await fetch(question._links?.self.href, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(examAnswerData)
    });

    if (response.ok) {
      // Mark question as completed and locked
      completedQuestions.value.add(questionIndex);
      console.log(`Question ${questionIndex + 1} submitted and locked`);
    } else {
      console.error('Failed to submit answer:', response.statusText);
    }
  } catch (error) {
    console.error('Error submitting exam answer:', error);
  } finally {
    isSubmittingAnswer.value = false;
  }
};

// Custom next handler for exam mode
const handleExamNext = async (nextCallback: () => void, questionIndex: number) => {
  if (isCurrentlyExamMode.value) {
    // Submit current answer to backend
    await submitExamAnswer(questionIndex);
    
    // Move to next question
    currentQuestionIndex.value++;
    nextCallback();
  } else {
    // Normal test mode navigation
    nextCallback();
  }
};

const handleClose = () => {
	if (isCurrentlyExamMode.value) {
		// For exam mode, emit submit with results
		const examResults = {
			answers: selectedAnswers.value,
			completedAt: new Date().toISOString(),
			questionsAnswered: selectedAnswers.value.filter(answer => answer !== -1).length,
			totalQuestions: questions.value.length,
			mode: 'exam'
		};
		emits('submit', examResults);
	}
	emits( 'close' );
};
</script>

<style scoped>
/* .p-stepper {
	flex-basis: 50rem;
} */
</style>
